import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home as FiHome,
  Book as FiBook,
  Calendar as FiCalendar,
  Info as FiInfo,
  User as FiUser,
  Search as FiSearch,
  ChevronDown as FiChevronDown,
  LogIn as FiLogIn,
  X as FiX,
  Plus as FiPlus,
  Bell as FiBell,
  Award as FiAward,
  Mail as FiMail,
  Briefcase as FiBriefcase,
  HelpCircle as FaHandsHelping
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import NotificationComponent from './Notification';
import ThemeToggle from './ThemeToggle';
import { SUBJECTS } from '../config/subjects';
import { cn } from '../lib/utils';



const Navbar = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { isAdmin, isCompany, userRole, user, userProfile } = useAuth();


  const searchResults = searchQuery.trim()
    ? SUBJECTS.filter(s =>
      s.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.abbr && s.abbr.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    : [];

  const c = {
    bg: 'var(--bg-main)',
    surface: 'var(--bg-card)',
    surfaceHover: 'var(--bg-surface)',
    border: 'var(--border)',
    text: 'var(--text-main)',
    textMuted: 'var(--text-muted)',
    accent: 'var(--accent)',
    glass: 'var(--glass)',
    glassBorder: 'var(--glass-border)',
  };



  useEffect(() => { setResourcesOpen(false); setSearchOpen(false); }, [location]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const mainLinks = isCompany
    ? [
      { to: '/home', label: 'Home' },
      { to: '/job-portal/company/dashboard', label: 'Job Hub' },
      { to: '/about', label: 'About' },
      { to: '/contactus', label: 'Contact' },
    ]
    : [
      { to: '/home', label: 'Home' },
      { to: '/tech-library', label: 'CodeCampus' },
      { to: '/job-portal/opportunities', label: 'Opportunities' },
      { to: '/ipu-result', label: 'IPU Result' },
      { to: '/event', label: 'Events' },
      { to: '/about', label: 'About' },
      { to: '/contactus', label: 'Contact' },
      ...(isAdmin ? [{ to: '/admin', label: 'Admin' }] : []),
    ];

  const resourceLinks = [
    { to: '/btech/cse/1st', label: 'CSE – 1st Sem', sub: 'Computer Science' },
    { to: '/btech/cse/2nd', label: 'CSE – 2nd Sem', sub: 'Computer Science' },
    { to: '/btech/cse/3rd', label: 'CSE – 3rd Sem', sub: 'Computer Science' },
    { to: '/btech/aiml/1st', label: 'AIML – 1st Sem', sub: 'AI & Machine Learning' },
    { to: '/btech/aiml/2nd', label: 'AIML – 2nd Sem', sub: 'AI & Machine Learning' },
    { to: '/btech/aids/1st', label: 'AIDS – 1st Sem', sub: 'AI & Data Science' },
    { to: '/btech/aids/2nd', label: 'AIDS – 2nd Sem', sub: 'AI & Data Science' },
    { to: '/btech/it/1st', label: 'IT – 1st Sem', sub: 'Information Technology' },
  ];

  const mobileTabs = isCompany
    ? [
      { to: '/home', label: 'Home', icon: <FiHome size={20} /> },
      { to: '/job-portal/company/dashboard', label: 'Hub', icon: <FiBriefcase size={20} /> },
      { id: 'fab', label: 'More', icon: <FiPlus size={24} /> },
      { to: '/contactus', label: 'Contact', icon: <FiMail size={20} /> },
      { to: user ? '/profile' : '/login', label: 'Account', icon: <FiUser size={20} /> },
    ]
    : [
      { to: '/home', label: 'Home', icon: <FiHome size={20} /> },
      { to: '/job-portal/opportunities', label: 'Jobs', icon: <FiBriefcase size={20} /> },
      { id: 'fab', label: 'More', icon: <FiPlus size={24} /> },
      { to: '/tech-library', label: 'Library', icon: <FiBook size={20} /> },
      { to: user ? '/profile' : '/login', label: 'Account', icon: <FiUser size={20} /> },
    ];

  const fabMenuItems = [
    { to: '/event', label: 'Events', icon: <FiCalendar size={18} /> },
    { to: '/contactus', label: 'Contact', icon: <FiMail size={18} /> },
    { to: '/about', label: 'About', icon: <FiInfo size={18} /> },
    ...(isAdmin ? [{ to: '/admin', label: 'Admin', icon: <FiUser size={18} /> }] : []),
  ];

  return (
    <>
      <style>{`
        .snaped-navbar-desktop { display: flex !important; }
        .snaped-navbar-mobile-top { display: none !important; }
        .snaped-navbar-mobile-bottom { display: none !important; }
        .snaped-spacer-desktop { display: block !important; height: 64px; }
        .snaped-spacer-mobile { display: none !important; }
        @media (max-width: 1024px) {
          .snaped-navbar-desktop { display: none !important; }
          .snaped-navbar-mobile-top { display: flex !important; }
          .snaped-navbar-mobile-bottom { display: flex !important; }
          .snaped-spacer-desktop { display: none !important; }
          .snaped-spacer-mobile { display: block !important; height: 56px; }
          body { padding-bottom: 85px !important; }
        }
        .nav-link-item:hover { color: var(--text-main) !important; }
        .nav-link-pill { position: relative; }
        .nav-link-pill::after { 
          content: ''; position: absolute; bottom: 4px; left: 16px; right: 16px; 
          height: 2px; background: hsl(var(--primary)); border-radius: 2px; 
          opacity: 0; transform: scaleX(0); transition: all 0.3s ease;
          box-shadow: 0 0 8px hsl(var(--primary));
        }
        .nav-link-pill.active::after { opacity: 1; transform: scaleX(1); }
        .nav-dropdown-item:hover { background: var(--bg-surface) !important; }
      `}</style>

      {/* ── DESKTOP TOPBAR ───────────────────────────────────────────── */}
      <motion.header
        className="snaped-navbar-desktop fixed top-0 left-0 right-0 h-16 items-center justify-between px-8 bg-background/80 backdrop-blur-xl border-b border-border z-[9999] transition-all duration-300 gap-5"
        initial={{ y: -64 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 28 }}
      >
        {/* Logo */}
        <motion.div
          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/home')}
          className="cursor-pointer flex items-center gap-2.5 flex-shrink-0"
        >
          <img
            src={require('../assets/LOGO SnapED.png')}
            alt="SnapED Logo"
            className="h-8 w-auto object-contain"
          />
        </motion.div>

        {/* Centre links */}
        <nav className="flex items-center gap-1.5 flex-1 justify-center">
          {mainLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={cn(
                "nav-link-item nav-link-pill px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap tracking-tight",
                isActive(to) ? "text-foreground" : "text-muted-foreground"
              )}
            >
              {label}
            </Link>
          ))}

          {/* Resources dropdown - Hide for companies */}
          {!isCompany && (
            <div className="relative">
              <button
                onClick={() => setResourcesOpen(v => !v)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                Resources
                <FiChevronDown
                  size={13}
                  className={cn("transition-transform duration-200", resourcesOpen && "rotate-180")}
                />
              </button>
              <AnimatePresence>
                {resourcesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.96 }}
                    transition={{ duration: 0.14 }}
                    className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 bg-card border border-border rounded-xl p-1.5 min-w-[230px] shadow-2xl z-[1100]"
                  >
                    {resourceLinks.map(({ to, label, sub }) => (
                      <Link
                        key={to} to={to}
                        className="block px-2.5 py-2 rounded-lg hover:bg-accent transition-colors no-underline"
                      >
                        <div className="text-[13px] font-bold text-foreground">{label}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Search */}
          {!isCompany && (
            <div className="relative">
              <AnimatePresence mode="wait">
                {searchOpen ? (
                  <motion.div
                    key="open"
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 240, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'circOut' }}
                    className="flex items-center relative"
                  >
                    <input
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search subjects..."
                      className="w-full h-10 bg-accent/50 border border-border rounded-xl px-4 pr-10 text-sm outline-none focus:border-primary transition-colors"
                    />
                    <FiX
                      size={15}
                      onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                      className="absolute right-3 cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
                    />
                  </motion.div>
                ) : (
                  <motion.button
                    key="icon"
                    whileHover={{ scale: 1.05, backgroundColor: 'rgba(var(--primary), 0.1)' }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSearchOpen(true)}
                    className="w-10 h-10 rounded-xl bg-accent/30 border border-border text-muted-foreground hover:text-foreground flex items-center justify-center transition-all duration-200"
                  >
                    <FiSearch size={18} />
                  </motion.button>
                )}
              </AnimatePresence>

              {/* Search Results Dropdown */}
              <AnimatePresence>
                {searchOpen && searchResults.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="absolute top-[calc(100%+12px)] right-0 w-[320px] bg-[#0f172a] border-2 border-primary/40 rounded-2xl p-2 shadow-[0_20px_50px_rgba(0,0,0,0.8)] z-[1200] max-h-[400px] overflow-y-auto"
                  >
                    <div className="px-3 py-2 text-[10px] font-black text-primary uppercase tracking-widest flex justify-between border-b border-border/50 mb-1">
                      <span>Results ({searchResults.length})</span>
                      <span className="font-normal opacity-70">ESC to close</span>
                    </div>
                    {searchResults.map((result) => (
                      <div
                        key={result.path}
                        onClick={() => {
                          navigate(result.path);
                          setSearchOpen(false);
                          setSearchQuery('');
                        }}
                        className="p-3 rounded-xl cursor-pointer hover:bg-primary/10 hover:border-primary/20 border border-transparent transition-all flex flex-col gap-0.5 mb-1 group"
                      >
                        <div className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">{result.label}</div>
                        <div className="text-[11px] text-muted-foreground">{result.sub}</div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <ThemeToggle />
          <NotificationComponent />

          <motion.div
            onClick={() => navigate(isCompany ? '/job-portal/company/dashboard' : (user ? '/profile?tab=contribution' : '/login'))}
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            className="h-10 px-5 bg-primary hover:bg-primary/90 rounded-xl text-primary-foreground text-sm font-black flex items-center gap-2 cursor-pointer shadow-lg shadow-primary/20 transition-all whitespace-nowrap"
          >
            {isCompany ? <><FiBriefcase size={14} /> Post Job</> : <><FaHandsHelping size={14} /> Contribute</>}
          </motion.div>

          {user ? (
            <Link to="/profile" className="no-underline">
              <motion.div
                whileHover={{ scale: 1.06 }}
                className="w-10 h-10 rounded-full bg-primary overflow-hidden cursor-pointer flex items-center justify-center border-2 border-primary/20"
              >
                {userProfile?.photo || user?.photoURL
                  ? <img src={userProfile?.photo || user?.photoURL} alt="av" className="w-full h-full object-cover" />
                  : <FiUser size={18} className="text-primary-foreground" />}
              </motion.div>
            </Link>
          ) : (
            <Link to="/login" className="no-underline">
              <motion.button
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                className="h-10 px-4 bg-muted border border-border rounded-xl text-foreground text-sm font-bold cursor-pointer flex items-center gap-2 hover:bg-accent transition-all"
              >
                <FiLogIn size={16} /> Sign In
              </motion.button>
            </Link>
          )}
        </div>
      </motion.header>

      <div className="snaped-navbar-mobile-top fixed top-0 left-0 right-0 h-14 items-center justify-between px-4 bg-background/80 backdrop-blur-xl border-b border-border z-[10001] transition-all duration-300">
        <AnimatePresence mode="wait">
          {searchOpen ? (
            <motion.div
              key="mobile-search-bar"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center w-full gap-2.5"
            >
              <div className="relative flex-1">
                <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search subjects..."
                  className="w-full h-9 bg-accent/50 border border-border rounded-lg pl-9 pr-10 text-sm outline-none"
                />
                <FiX
                  size={16}
                  onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground"
                />
              </div>
            </motion.div>
          ) : (
            <>
              <div onClick={() => navigate('/home')} className="flex items-center gap-2 cursor-pointer">
                <img
                  src={require('../assets/LOGO SnapED.png')}
                  alt="SnapED Logo"
                  className="h-7 w-auto object-contain"
                />
              </div>
              <div className="flex items-center gap-1.5">
                {!isCompany && (
                  <motion.button
                    whileTap={{ scale: 0.94 }}
                    onClick={() => setSearchOpen(true)}
                    className="w-9 h-9 rounded-lg bg-accent/30 border border-border text-muted-foreground flex items-center justify-center cursor-pointer"
                  >
                    <FiSearch size={18} />
                  </motion.button>
                )}
                <ThemeToggle />
                <NotificationComponent />
                <motion.div
                  onClick={() => navigate(isCompany ? '/job-portal/company/dashboard' : (user ? '/profile?tab=contribution' : '/login'))}
                  whileTap={{ scale: 0.94 }}
                  className="h-9 px-3 bg-primary rounded-lg text-primary-foreground text-[10px] font-black flex items-center gap-1.5 cursor-pointer shadow-lg shadow-primary/10 mobile-top-btn"
                >
                  {isCompany ? (
                    <><FiBriefcase size={16} /><span className="mobile-contribute-text uppercase tracking-tight">Post Job</span></>
                  ) : (
                    <><FaHandsHelping size={16} /><span className="mobile-contribute-text uppercase tracking-tight">Contribute</span></>
                  )}
                </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>

        {/* Mobile Search Results Dropdown */}
        <AnimatePresence>
          {searchOpen && searchResults.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-[52px] left-0 right-0 bg-[#0f172a] border-b-2 border-primary/40 p-4 pb-6 shadow-2xl max-h-[calc(100vh-120px)] overflow-y-auto z-[10000]"
            >
              <div className="py-2 text-[10px] font-black text-primary uppercase tracking-widest border-b border-border/50 mb-3">
                Found {searchResults.length} Results
              </div>
              {searchResults.map((result) => (
                <div
                  key={result.path}
                  onClick={() => {
                    navigate(result.path);
                    setSearchOpen(false);
                    setSearchQuery('');
                  }}
                  className="py-4 border-b border-border/50 last:border-0 cursor-pointer active:bg-primary/10 px-2 rounded-lg transition-colors"
                >
                  <div className="text-sm font-bold text-foreground">{result.label}</div>
                  <div className="text-[11px] text-muted-foreground">{result.sub}</div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── MOBILE BOTTOM TAB BAR ────────────────────────────────────── */}
      <AnimatePresence>
        {fabOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setFabOpen(false)}
              className="fixed inset-0 bg-background/40 backdrop-blur-md z-[9997]"
            />
            {/* FAB Menu - Radial Layout */}
            <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[9999]">
              {fabMenuItems.map((item, idx) => {
                const total = fabMenuItems.length;
                const radius = 115; // Increased distance from center
                // Distribute items in a tighter semicircle (from 165 to 15 degrees) to avoid edge overlap
                const angle = 165 - (idx * (150 / (total - 1)));
                const radian = (angle * Math.PI) / 180;
                const x = radius * Math.cos(radian);
                const y = -radius * Math.sin(radian);

                return (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
                    animate={{ opacity: 1, x, y, scale: 1 }}
                    exit={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
                    transition={{
                      type: 'spring',
                      stiffness: 260,
                      damping: 20,
                      delay: idx * 0.04
                    }}
                    className="absolute top-0 left-0"
                  >
                    <Link
                      to={item.to}
                      onClick={() => setFabOpen(false)}
                      className="flex flex-col items-center gap-1.5 no-underline -translate-x-1/2 -translate-y-1/2 w-[70px]"
                    >
                      <div className="w-[52px] h-[52px] rounded-full bg-card backdrop-blur-md border border-border text-primary flex items-center justify-center shadow-2xl transition-all hover:scale-110">
                        {item.icon}
                      </div>
                      <span className="text-[10px] font-black text-foreground uppercase tracking-widest text-center">
                        {item.label}
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </AnimatePresence>

      <div className="snaped-navbar-mobile-bottom fixed bottom-5 left-4 right-4 h-16 flex items-center justify-around bg-background/80 backdrop-blur-2xl border border-border rounded-[1.5rem] z-[9998] shadow-2xl transition-all duration-300">
        {mobileTabs.map((tab) => {
          if (tab.id === 'fab') {
            return (
              <button
                key="fab"
                onClick={() => setFabOpen(!fabOpen)}
                className={cn(
                  "w-14 h-14 rounded-full flex items-center justify-center border-none cursor-pointer -translate-y-6 shadow-2xl transition-all duration-300",
                  fabOpen ? "bg-destructive rotate-45" : "bg-primary"
                )}
              >
                <FiPlus size={28} className="text-primary-foreground" />
              </button>
            );
          }

          const active = isActive(tab.to);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={cn(
                "flex flex-col items-center justify-center gap-1 flex-1 py-2 no-underline transition-all duration-200",
                active ? "text-primary" : "text-muted-foreground"
              )}
            >
              <div className={cn(
                "transition-all duration-300",
                active && "scale-110 -translate-y-0.5"
              )}>
                {tab.icon}
              </div>
              <span className="text-[9px] font-black uppercase tracking-tight">
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>

      {/* Spacers */}
      <div className="snaped-spacer-desktop" />
      <div className="snaped-spacer-mobile" />
    </>
  );
};

export default Navbar;
